/**
 * 租户管理（平台运营端）
 *
 * 平台运营人员能看到所有租户的概要，这里必须守住两条边界：
 *   1. **跨租户访问留痕**：查看租户详情、调整配额、变更套餐都会写入平台审计日志
 *      （分类：跨租户访问 / 配置变更），页面上明确告知操作人；
 *   2. **停用是有业务后果的动作**：停用后该租户所有用户无法登录，但数据保留，
 *      因此必须填写原因，原因会展示在租户列表上供其它运营人员接手判断。
 */

import { useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Col,
  Descriptions,
  Divider,
  Drawer,
  Form,
  Input,
  InputNumber,
  Modal,
  Row,
  Select,
  Space,
  Statistic,
  Switch,
  Table,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import {
  AuditOutlined,
  EyeOutlined,
  PauseCircleOutlined,
  PlayCircleOutlined,
  ReloadOutlined,
  SwapOutlined,
  TeamOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import {
  ISOLATION_MODE_LABELS,
  PLAN_TIER_LABELS,
  PROVIDER_LABELS,
  PermissionCode,
  PlanTier,
  TENANT_STATUS_LABELS,
  TenantStatus,
  type Plan,
  type Tenant,
  type TenantListItem,
} from '@ilc/shared';
import { platformApi } from '@/api/endpoints';
import { mockPlanList, mockTenantDetail, mockTenantList } from '@/mocks';
import { usePagedList } from '@/hooks/usePagedList';
import { useResource } from '@/hooks/useResource';
import { useAuthStore } from '@/stores/auth';
import { DataTable } from '@/components/DataTable';
import { PageHeader } from '@/components/PageHeader';
import { ConfirmDanger } from '@/components/ConfirmDanger';
import { MaskedField } from '@/components/MaskedField';
import { PageState } from '@/components/PageState';
import { performAction } from '@/utils/performAction';

interface Query {
  page?: number;
  pageSize?: number;
  keyword?: string;
  status?: TenantStatus;
  planTier?: PlanTier;
}

const STATUS_COLORS: Record<TenantStatus, string> = {
  PENDING_VERIFY: 'orange',
  ACTIVE: 'green',
  SUSPENDED: 'red',
  ARCHIVED: 'default',
};

/** 套餐档位与隔离模式的对应关系（文档 9.1/2.2） */
const TIER_ISOLATION: Record<PlanTier, string> = {
  STARTER: ISOLATION_MODE_LABELS.SHARED,
  STANDARD: ISOLATION_MODE_LABELS.SCHEMA,
  PROFESSIONAL: ISOLATION_MODE_LABELS.DATABASE,
  ENTERPRISE: ISOLATION_MODE_LABELS.DATABASE,
};

export default function Tenants(): React.ReactElement {
  const can = useAuthStore((s) => s.can);
  const [detail, setDetail] = useState<TenantListItem | null>(null);
  const [suspendTarget, setSuspendTarget] = useState<TenantListItem | null>(null);
  const [verifyTarget, setVerifyTarget] = useState<TenantListItem | null>(null);
  const [planTarget, setPlanTarget] = useState<TenantListItem | null>(null);
  const [quotaTarget, setQuotaTarget] = useState<TenantListItem | null>(null);
  const [resetTarget, setResetTarget] = useState<TenantListItem | null>(null);
  /** 重置成功后展示的新密码。只在本次会话内存里，刷新即消失——不落任何本地存储 */
  const [issuedPassword, setIssuedPassword] = useState<{ account: string; password: string } | null>(
    null,
  );
  const [saving, setSaving] = useState(false);
  const [verifyForm] = Form.useForm<{ approved: boolean; reason?: string }>();
  const [planForm] = Form.useForm<{ planId: string; prorate?: boolean }>();
  const [resetForm] = Form.useForm<{ newPassword?: string }>();
  const [quotaForm] = Form.useForm<{
    provider: string;
    monthlyLimit: number;
    warnThreshold?: number;
    enabled?: boolean;
  }>();

  const list = usePagedList<TenantListItem, Query>({
    loader: (q) => platformApi.tenants(q),
    fallback: (q) => mockTenantList(q),
    initialQuery: { page: 1, pageSize: 20 },
  });

  const plans = useResource<Plan[]>(() => platformApi.plans(), { fallback: () => mockPlanList() });

  const tenantDetail = useResource<Tenant>(
    () => platformApi.tenant(detail?.id ?? ''),
    {
      fallback: () => mockTenantDetail(detail?.id ?? ''),
      enabled: detail !== null,
    },
  );

  const stats = {
    total: list.total,
    pending: list.list.filter((t) => t.status === TenantStatus.PENDING_VERIFY).length,
    suspended: list.list.filter((t) => t.status === TenantStatus.SUSPENDED).length,
    quotaWarning: list.list.filter((t) => t.quotaHealth >= 0.9).length,
  };

  const changePlan = async (): Promise<void> => {
    if (!planTarget) return;
    const values = await planForm.validateFields();
    setSaving(true);
    const result = await performAction({
      action: () => platformApi.changePlan(planTarget.id, { planId: values.planId, prorate: values.prorate }),
      successMessage: '租户套餐已变更，新的配额与模块授权立即生效（隔离模式变更可能需要运维配合）',
      offline: () => undefined,
      offlineMessage: '后端平台接口尚未就绪，本次变更仅在本地演示，未写入数据库',
    });
    setSaving(false);
    if (result.ok) {
      setPlanTarget(null);
      list.reload();
    }
  };

  /**
   * 重置租户管理员密码。
   *
   * 与「审核认证」的区别要说清楚：审核是状态迁移（待认证 → 正常，一个租户
   * 只能做一次），重置密码是可反复执行的日常运维，不改变租户状态。
   * 企业忘记密码时走这里，不要重新审核。
   */
  const resetAdminPassword = async (): Promise<void> => {
    if (!resetTarget) return;
    const values = await resetForm.validateFields();
    setSaving(true);
    const result = await performAction({
      action: () => platformApi.resetAdminPassword(resetTarget.id, { newPassword: values.newPassword }),
      successMessage: '管理员密码已重置',
      offline: () => undefined,
      offlineMessage: '后端平台接口尚未就绪，本次重置仅在本地演示',
    });
    setSaving(false);
    if (result.ok) {
      setResetTarget(null);
      resetForm.resetFields();
      // 新密码只在这里出现一次，后端不落库明文、也不提供再次查询
      if (result.data) {
        setIssuedPassword({ account: result.data.adminUsername, password: result.data.newPassword });
      }
    }
  };

  const adjustQuota = async (): Promise<void> => {
    if (!quotaTarget) return;
    const values = await quotaForm.validateFields();
    setSaving(true);
    const result = await performAction({
      action: () =>
        platformApi.adjustQuota(quotaTarget.id, {
          provider: values.provider as never,
          monthlyLimit: Number(values.monthlyLimit),
          warnThreshold: values.warnThreshold,
          enabled: values.enabled,
        }),
      successMessage: '接口配额已调整，超出配额时该接口的调用会被平台拦截并记入调用日志',
      offline: () => undefined,
      offlineMessage: '后端平台接口尚未就绪，本次调整仅在本地演示',
    });
    setSaving(false);
    if (result.ok) {
      setQuotaTarget(null);
      list.reload();
    }
  };

  const verifyTenant = async (approved: boolean, reason?: string): Promise<void> => {
    if (!verifyTarget) return;
    const result = await performAction({
      action: () => platformApi.verifyTenant(verifyTarget.id, { approved, reason }),
      successMessage: approved ? '租户认证已通过，租户可正常使用' : '已驳回认证申请，驳回原因会通知租户联系人',
      offline: () => undefined,
      offlineMessage: '后端平台接口尚未就绪，本次审核仅在本地演示',
    });
    if (result.ok) {
      setVerifyTarget(null);
      verifyForm.resetFields();
      list.reload();
    }
  };

  const suspendTenant = async (reason: string): Promise<void> => {
    if (!suspendTarget) return;
    const result = await performAction({
      action: () => platformApi.suspendTenant(suspendTarget.id, { reason }),
      successMessage: '租户已停用，该租户所有用户将无法登录（数据保留）',
      offline: () => undefined,
      offlineMessage: '后端平台接口尚未就绪，本次停用仅在本地演示',
    });
    setSuspendTarget(null);
    if (result.ok) list.reload();
  };

  const resumeTenant = async (tenant: TenantListItem): Promise<void> => {
    const result = await performAction({
      action: () => platformApi.verifyTenant(tenant.id, { approved: true, reason: '恢复启用' }),
      successMessage: '租户已恢复启用',
      offline: () => undefined,
      offlineMessage: '后端平台接口尚未就绪，本次操作仅在本地演示',
    });
    if (result.ok) list.reload();
  };

  return (
    <div className="ilc-page">
      <PageHeader
        title="租户管理"
        description="平台运营端：审核租户资质、调整套餐与接口配额、停用违规或欠费租户。所有跨租户操作均写入平台审计日志。"
        tags={<Tag icon={<TeamOutlined />} color="purple">平台运营</Tag>}
        extra={
          <Button icon={<ReloadOutlined />} onClick={list.reload}>
            刷新
          </Button>
        }
      />

      <Alert
        type="info"
        showIcon
        icon={<AuditOutlined />}
        style={{ marginBottom: 12 }}
        message="平台运营操作会被审计"
        description="查看租户详情属于「跨租户访问」，调整套餐与配额属于「配置变更」，都会记录操作人、时间、目标租户与 traceId，供事后审计。请勿在非工单场景下浏览租户数据。"
      />

      <Row gutter={[16, 16]} style={{ marginBottom: 12 }}>
        <Col xs={12} lg={6}>
          <Card size="small">
            <Statistic title="租户总数" value={stats.total} suffix="家" />
          </Card>
        </Col>
        <Col xs={12} lg={6}>
          <Card size="small">
            <Statistic
              title="待认证（本页）"
              value={stats.pending}
              suffix="家"
              valueStyle={{ color: stats.pending > 0 ? '#d46b08' : undefined }}
            />
          </Card>
        </Col>
        <Col xs={12} lg={6}>
          <Card size="small">
            <Statistic
              title="已停用（本页）"
              value={stats.suspended}
              suffix="家"
              valueStyle={{ color: stats.suspended > 0 ? '#cf1322' : undefined }}
            />
          </Card>
        </Col>
        <Col xs={12} lg={6}>
          <Card size="small">
            <Statistic
              title="配额告警（本页）"
              value={stats.quotaWarning}
              suffix="家"
              valueStyle={{ color: stats.quotaWarning > 0 ? '#cf1322' : undefined }}
              prefix={stats.quotaWarning > 0 ? <WarningOutlined /> : undefined}
            />
          </Card>
        </Col>
      </Row>

      <DataTable<TenantListItem>
        storageKey="platform-tenants"
        exportFileName="租户列表"
        exportColumns={[
          { title: '企业名称', dataIndex: 'name' },
          { title: '租户标识', dataIndex: 'creditCode' },
          { title: '统一社会信用代码', dataIndex: 'creditCode' },
          { title: '状态', value: (r) => TENANT_STATUS_LABELS[r.status] },
          { title: '套餐', dataIndex: 'planName' },
          { title: '本月发票量', dataIndex: 'invoiceCountThisMonth' },
          { title: '用户数', dataIndex: 'userCount' },
        ]}
        columns={[
          {
            title: '企业名称',
            dataIndex: 'name',
            width: 220,
            fixed: 'left',
            render: (v: string, record) => (
              <Space direction="vertical" size={0}>
                <span>{v}</span>
                <Space size={4}>
                  <Tag className="ilc-mono" style={{ marginInlineEnd: 0 }}>
                    {record.code}
                  </Tag>
                  {record.remark ? (
                    <Typography.Text type="secondary" style={{ fontSize: 11 }}>
                      {record.remark}
                    </Typography.Text>
                  ) : null}
                </Space>
              </Space>
            ),
          },
          {
            title: '统一社会信用代码',
            dataIndex: 'creditCode',
            width: 190,
            render: (v: string) => (
              <MaskedField value={v} kind="taxNo" permission={PermissionCode.TENANT_CONFIG_WRITE} />
            ),
          },
          {
            title: '状态',
            dataIndex: 'status',
            width: 110,
            render: (v: TenantStatus, record) => (
              <Space direction="vertical" size={2}>
                <Tag color={STATUS_COLORS[v]}>{TENANT_STATUS_LABELS[v]}</Tag>
                {v === TenantStatus.SUSPENDED && record.suspendedReason ? (
                  <Tooltip title={record.suspendedReason}>
                    <Typography.Text type="secondary" style={{ fontSize: 11 }} ellipsis>
                      停用原因见详情
                    </Typography.Text>
                  </Tooltip>
                ) : null}
              </Space>
            ),
          },
          {
            title: '套餐 / 隔离模式',
            key: 'plan',
            width: 200,
            render: (_v, record) => (
              <Space direction="vertical" size={0}>
                <span>{record.planName ?? '未订阅'}</span>
                <Typography.Text type="secondary" style={{ fontSize: 11 }}>
                  {ISOLATION_MODE_LABELS[record.isolationMode]}
                  {record.isolationTarget ? ` · ${record.isolationTarget}` : ''}
                </Typography.Text>
              </Space>
            ),
          },
          {
            title: '本月发票量',
            dataIndex: 'invoiceCountThisMonth',
            width: 120,
            align: 'right',
            // 平台统计字段后端还在补齐中，缺失时显示「-」而不是 0，避免把「没有数据」误读成「没有业务」
            render: (v?: number) =>
              v === undefined || v === null ? (
                <Tooltip title="平台统计接口暂未返回该字段">
                  <span className="ilc-num">-</span>
                </Tooltip>
              ) : (
                <span className="ilc-num">{v.toLocaleString('zh-CN')}</span>
              ),
          },
          {
            title: '接口用量健康度',
            dataIndex: 'quotaHealth',
            width: 150,
            render: (v?: number) => {
              if (v === undefined || v === null) {
                return (
                  <Tooltip title="平台统计接口暂未返回该字段">
                    <span className="ilc-num">-</span>
                  </Tooltip>
                );
              }
              const percent = Math.round(v * 100);
              const color = percent >= 90 ? '#cf1322' : percent >= 70 ? '#d46b08' : '#389e0d';
              return (
                <Space direction="vertical" size={0} style={{ width: '100%' }}>
                  <span className="ilc-num" style={{ color }}>
                    {percent}%
                  </span>
                  <Typography.Text type="secondary" style={{ fontSize: 11 }}>
                    {percent >= 90 ? '接近或已超配额，需关注' : '正常'}
                  </Typography.Text>
                </Space>
              );
            },
          },
          {
            title: '用户数',
            dataIndex: 'userCount',
            width: 90,
            align: 'right',
            render: (v?: number) => <span className="ilc-num">{v ?? '-'}</span>,
          },
          {
            title: '联系人',
            key: 'contact',
            width: 170,
            render: (_v, record) => (
              <Space direction="vertical" size={0}>
                <span>{record.contactName}</span>
                <MaskedField value={record.contactPhone} kind="phone" />
              </Space>
            ),
          },
          {
            title: '操作',
            key: 'action',
            width: 300,
            fixed: 'right',
            render: (_v, record) => (
              <Space size={4} wrap>
                <Button size="small" type="link" icon={<EyeOutlined />} onClick={() => setDetail(record)}>
                  详情
                </Button>
                {record.status === TenantStatus.PENDING_VERIFY ? (
                  <Button
                    size="small"
                    type="link"
                    onClick={() => {
                      setVerifyTarget(record);
                      verifyForm.setFieldsValue({ approved: true });
                    }}
                  >
                    审核认证
                  </Button>
                ) : null}
                <Button
                  size="small"
                  type="link"
                  icon={<SwapOutlined />}
                  disabled={!can(PermissionCode.TENANT_CONFIG_WRITE)}
                  onClick={() => {
                    setPlanTarget(record);
                    planForm.setFieldsValue({
                      planId: (plans.data ?? []).find((p) => p.name === record.planName)?.id ?? '',
                      prorate: true,
                    });
                  }}
                >
                  变更套餐
                </Button>
                <Button
                  size="small"
                  type="link"
                  disabled={!can(PermissionCode.PROVIDER_CONFIG_WRITE)}
                  onClick={() => {
                    setQuotaTarget(record);
                    quotaForm.setFieldsValue({ provider: 'OCR', monthlyLimit: 1000, warnThreshold: 0.8, enabled: true });
                  }}
                >
                  调整配额
                </Button>
                {/* 企业忘记密码时的唯一出路。未认证的租户还没有管理员账号，所以不给入口 */}
                <Button
                  size="small"
                  type="link"
                  disabled={record.status === TenantStatus.PENDING_VERIFY}
                  title={
                    record.status === TenantStatus.PENDING_VERIFY
                      ? '该租户尚未认证，管理员账号还不存在'
                      : undefined
                  }
                  onClick={() => {
                    setResetTarget(record);
                    resetForm.resetFields();
                  }}
                >
                  重置管理员密码
                </Button>
                {record.status === TenantStatus.SUSPENDED ? (
                  <Button
                    size="small"
                    type="link"
                    icon={<PlayCircleOutlined />}
                    onClick={() => void resumeTenant(record)}
                  >
                    恢复
                  </Button>
                ) : (
                  <Button
                    size="small"
                    type="link"
                    danger
                    icon={<PauseCircleOutlined />}
                    onClick={() => setSuspendTarget(record)}
                  >
                    停用
                  </Button>
                )}
              </Space>
            ),
          },
        ]}
        dataSource={list.list}
        rowKey="id"
        loading={list.loading}
        error={list.error}
        isFallback={list.isFallback}
        fallbackReason={list.fallbackReason}
        onRetry={list.reload}
        pagination={list.pagination}
        onChange={list.handleTableChange}
        emptyText="没有匹配的租户"
        toolbar={
          <Space wrap>
            <Input.Search
              placeholder="企业名称 / 租户标识 / 信用代码"
              allowClear
              style={{ width: 260 }}
              onSearch={(value) => list.setQuery({ keyword: value })}
            />
            <Select
              placeholder="状态"
              allowClear
              style={{ width: 130 }}
              value={list.query.status}
              onChange={(value) => list.setQuery({ status: value })}
              options={Object.entries(TENANT_STATUS_LABELS).map(([value, label]) => ({ value, label }))}
            />
            <Select
              placeholder="套餐档位"
              allowClear
              style={{ width: 130 }}
              value={list.query.planTier}
              onChange={(value) => list.setQuery({ planTier: value })}
              options={Object.entries(PLAN_TIER_LABELS).map(([value, label]) => ({ value, label }))}
            />
            <Button onClick={list.resetQuery}>重置</Button>
          </Space>
        }
      />

      {/* 租户详情：跨租户访问，会记入审计日志 */}
      <Drawer
        open={detail !== null}
        title={`租户详情 —— ${detail?.name ?? ''}`}
        onClose={() => setDetail(null)}
        width={640}
      >
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 12 }}
          message="本次查看已记入平台审计日志（跨租户访问）"
          description="平台运营人员访问租户数据会被记录操作人、目标租户与 traceId。请在工单或明确业务需要的前提下查看。"
        />
        <PageState
          loading={tenantDetail.loading}
          error={tenantDetail.error}
          onRetry={tenantDetail.reload}
          skeleton
          rows={6}
        >
          {tenantDetail.data ? (
            <>
              <Descriptions column={1} size="small" bordered>
                <Descriptions.Item label="企业名称">{tenantDetail.data.name}</Descriptions.Item>
                <Descriptions.Item label="租户标识">
                  <span className="ilc-mono">{tenantDetail.data.code}</span>
                </Descriptions.Item>
                <Descriptions.Item label="统一社会信用代码">
                  <span className="ilc-mono">{tenantDetail.data.creditCode}</span>
                </Descriptions.Item>
                <Descriptions.Item label="状态">
                  <Tag color={STATUS_COLORS[tenantDetail.data.status]}>
                    {TENANT_STATUS_LABELS[tenantDetail.data.status]}
                  </Tag>
                </Descriptions.Item>
                <Descriptions.Item label="隔离模式">
                  {ISOLATION_MODE_LABELS[tenantDetail.data.isolationMode]}
                  {tenantDetail.data.isolationTarget ? ` · ${tenantDetail.data.isolationTarget}` : ''}
                </Descriptions.Item>
                <Descriptions.Item label="联系人">
                  {tenantDetail.data.contactName} · {tenantDetail.data.contactPhone}
                  {tenantDetail.data.contactEmail ? ` · ${tenantDetail.data.contactEmail}` : ''}
                </Descriptions.Item>
                <Descriptions.Item label="认证情况">
                  {tenantDetail.data.verifiedAt ? (
                    <>
                      已于 <span className="ilc-mono">{tenantDetail.data.verifiedAt.slice(0, 10)}</span> 通过认证（
                      {tenantDetail.data.verifiedBy ?? '平台运营'}）
                    </>
                  ) : (
                    <Tag color="orange">尚未通过认证</Tag>
                  )}
                </Descriptions.Item>
                <Descriptions.Item label="管理员账号">
                  <span className="ilc-mono">{tenantDetail.data.adminUsername ?? '-'}</span>
                </Descriptions.Item>
                <Descriptions.Item label="停用原因">
                  {tenantDetail.data.suspendedReason ?? '（未停用）'}
                </Descriptions.Item>
                <Descriptions.Item label="平台备注">{tenantDetail.data.remark ?? '-'}</Descriptions.Item>
              </Descriptions>
              <Divider />
              <Space direction="vertical" size={4}>
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  平台运营端只提供「管理租户」的能力，不提供查看租户发票明细的入口——
                  租户业务数据的查阅应由租户自身权限体系控制，平台侧仅保留审计用途。
                </Typography.Text>
              </Space>
            </>
          ) : null}
        </PageState>
      </Drawer>

      {/* 审核认证 */}
      <Modal
        open={verifyTarget !== null}
        title={`审核租户认证 —— ${verifyTarget?.name ?? ''}`}
        onCancel={() => {
          setVerifyTarget(null);
          verifyForm.resetFields();
        }}
        onOk={() =>
          void verifyForm.validateFields().then((values) => verifyTenant(values.approved, values.reason))
        }
        okText="提交审核结果"
        destroyOnHidden
      >
        <Form form={verifyForm} layout="vertical" preserve={false} initialValues={{ approved: true }}>
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 12 }}
            message="认证前请核对营业执照与统一社会信用代码是否一致"
            description="通过后租户即可正常使用；驳回会通知租户联系人，请在原因中写清需要补充的材料。"
          />
          <Form.Item name="approved" label="审核结果" valuePropName="checked">
            <Switch checkedChildren="通过" unCheckedChildren="驳回" />
          </Form.Item>
          <Form.Item
            noStyle
            shouldUpdate={(prev, next) => prev.approved !== next.approved}
          >
            {({ getFieldValue }) =>
              getFieldValue('approved') === false ? (
                <Form.Item
                  name="reason"
                  label="驳回原因（必填）"
                  rules={[{ required: true, message: '驳回必须填写原因' }, { min: 8, message: '至少 8 个字' }]}
                >
                  <Input.TextArea rows={3} placeholder="如：营业执照与填写的统一社会信用代码不一致，请重新上传。" />
                </Form.Item>
              ) : null
            }
          </Form.Item>
        </Form>
      </Modal>

      {/* 变更套餐 */}
      <Modal
        open={planTarget !== null}
        title={`变更套餐 —— ${planTarget?.name ?? ''}`}
        onCancel={() => setPlanTarget(null)}
        onOk={() => void changePlan()}
        confirmLoading={saving}
        okText="确认变更"
        destroyOnHidden
      >
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 12 }}
          message={`当前套餐：${planTarget?.planName ?? '未订阅'}`}
          description="变更套餐会同时改变接口配额、模块授权与隔离模式。隔离模式升级（共享库 → 独立 Schema → 独立库）需要运维配合迁移数据，请提前确认。"
        />
        <Form form={planForm} layout="vertical" preserve={false}>
          <Form.Item name="planId" label="目标套餐" rules={[{ required: true, message: '请选择目标套餐' }]}>
            <Select
              options={(plans.data ?? []).map((plan) => ({
                value: plan.id,
                label: `${plan.name}（${PLAN_TIER_LABELS[plan.tier]} · ${ISOLATION_MODE_LABELS[plan.isolationMode]}）`,
              }))}
            />
          </Form.Item>
          <Form.Item name="prorate" label="按比例折算费用" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Form>
        {plans.data && plans.data.length > 0 ? (
          <Table<Plan>
            size="small"
            rowKey="id"
            pagination={false}
            dataSource={plans.data}
            columns={[
              { title: '套餐', dataIndex: 'name', width: 100 },
              {
                title: '月费',
                dataIndex: 'priceCents',
                width: 110,
                align: 'right',
                render: (v: number) => <span className="ilc-num">¥{(v / 100).toFixed(2)}</span>,
              },
              { title: '用户上限', dataIndex: 'maxUsers', width: 90, align: 'right' },
              {
                title: '月发票上限',
                dataIndex: 'maxInvoicesPerMonth',
                width: 110,
                align: 'right',
                render: (v?: number) => <span className="ilc-num">{v === undefined || v === null ? '-' : v.toLocaleString('zh-CN')}</span>,
              },
              { title: '隔离', dataIndex: 'isolationMode', render: (v: Plan['isolationMode']) => ISOLATION_MODE_LABELS[v] },
            ]}
          />
        ) : null}
      </Modal>

      {/* 调整配额 */}
      <Modal
        open={quotaTarget !== null}
        title={`调整接口配额 —— ${quotaTarget?.name ?? ''}`}
        onCancel={() => setQuotaTarget(null)}
        onOk={() => void adjustQuota()}
        confirmLoading={saving}
        okText="确认调整"
        destroyOnHidden
      >
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 12 }}
          message="配额用尽时该接口的调用会被平台直接拦截"
          description="被拦截的调用会以「配额拦截」记入调用日志，对应发票的校验项会标记为「外部校验暂不可用」，需人工核验接管。"
        />
        <Form form={quotaForm} layout="vertical" preserve={false}>
          <Form.Item name="provider" label="接口" rules={[{ required: true, message: '请选择接口' }]}>
            <Select options={Object.entries(PROVIDER_LABELS).map(([value, label]) => ({ value, label }))} />
          </Form.Item>
          <Form.Item name="monthlyLimit" label="月度配额上限（次）" rules={[{ required: true, message: '请输入配额上限' }]}>
            <InputNumber style={{ width: '100%' }} min={0} step={100} />
          </Form.Item>
          <Form.Item name="warnThreshold" label="超额告警阈值（0-1）" extra="如 0.8 表示用量达到 80% 时告警">
            <InputNumber style={{ width: '100%' }} min={0} max={1} step={0.05} />
          </Form.Item>
          <Form.Item name="enabled" label="启用该接口配额控制" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Form>
      </Modal>

      {/* 重置管理员密码 */}
      <Modal
        open={resetTarget !== null}
        title={`重置管理员密码 —— ${resetTarget?.name ?? ''}`}
        onCancel={() => setResetTarget(null)}
        onOk={() => void resetAdminPassword()}
        confirmLoading={saving}
        okText="确认重置"
        destroyOnHidden
      >
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 12 }}
          message="重置与「审核认证」是两件事"
          description="审核是状态迁移（待认证 → 正常，一个租户只能做一次）；重置密码是可反复执行的日常运维，不改变租户状态。企业忘记密码时用这里，不要重新审核。"
        />
        <Form form={resetForm} layout="vertical" preserve={false}>
          <Form.Item
            name="newPassword"
            label="新密码"
            extra="留空则系统生成一个随机密码并显示给你。无论哪种方式，新密码只会出现一次，请立即通过可靠渠道（电话回访）转告企业管理员。"
            rules={[
              { min: 8, message: '密码至少 8 位' },
              { pattern: /[a-zA-Z]/, message: '密码需包含字母' },
              { pattern: /\d/, message: '密码需包含数字' },
            ]}
          >
            <Input.Password placeholder="留空则由系统生成" autoComplete="new-password" />
          </Form.Item>
        </Form>
      </Modal>

      {/* 新密码一次性展示 —— 后端不落库明文，关掉就再也看不到 */}
      <Modal
        open={issuedPassword !== null}
        title="新密码已生成"
        onCancel={() => setIssuedPassword(null)}
        onOk={() => setIssuedPassword(null)}
        okText="我已记录，关闭"
        cancelButtonProps={{ style: { display: 'none' } }}
        closable={false}
        maskClosable={false}
      >
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 12 }}
          message="这是唯一一次显示，关闭后无法再查看"
          description="系统不保存明文密码。请立即记录并通过可靠渠道（电话回访）告知企业管理员，不要用明文短信或群发邮件。"
        />
        <Descriptions column={1} size="small" bordered>
          <Descriptions.Item label="管理员账号">
            <span className="ilc-mono">{issuedPassword?.account}</span>
          </Descriptions.Item>
          <Descriptions.Item label="新密码">
            <Typography.Text
              className="ilc-mono"
              copyable={{ text: issuedPassword?.password ?? '' }}
              strong
            >
              {issuedPassword?.password}
            </Typography.Text>
          </Descriptions.Item>
        </Descriptions>
      </Modal>

      <ConfirmDanger
        open={suspendTarget !== null}
        title={`停用租户「${suspendTarget?.name ?? ''}」`}
        level="high"
        confirmText="确认停用"
        acknowledgeText="我已知晓：停用后该租户所有用户将无法登录，需人工恢复"
        description={
          <Space direction="vertical" size={4}>
            <span>
              停用后该租户的 <strong>{suspendTarget?.userCount ?? 0}</strong> 个用户立即无法登录，
              但<strong>数据全部保留</strong>，可在恢复后继续使用。
            </span>
            <span>停用期间该租户的接口调用会被拒绝，正在流程中的发票会停留在当前节点。</span>
            <span style={{ color: 'rgba(0,0,0,0.45)' }}>
              停用原因会展示在租户列表与详情中，供其它运营人员接手判断，请写清依据（如欠费账期、违规情形、工单号）。
            </span>
          </Space>
        }
        reasonLabel="停用原因（必填，不少于 10 字，将写入平台审计日志）"
        minReasonLength={10}
        reasonPresets={[
          '连续 2 个计费周期欠费，按合同约定暂停服务，数据保留 90 天',
          '存在发票异常使用情形，配合风控核查期间暂停服务（工单号见备注）',
        ]}
        loading={saving}
        onConfirm={(reason) => suspendTenant(reason)}
        onCancel={() => setSuspendTarget(null)}
      />

      <Typography.Paragraph type="secondary" style={{ fontSize: 12, marginTop: 12, marginBottom: 0 }}>
        <WarningOutlined /> 提示：平台运营端不提供「代租户操作业务数据」的能力。
        如租户反馈业务问题，请通过租户管理员或其技术实施组处理，避免平台侧越权操作。
      </Typography.Paragraph>
    </div>
  );
}
